/**
 * useProfile hook -- fetches the user's profile from GET /profile
 * and provides an updateProfile mutation method via PUT /profile.
 */

import { useState, useCallback, useEffect } from "react";
import { apiClient } from "../lib/api-client";
import type { UserProfile } from "../components/layout/sidebar/types";

export interface UseProfileReturn {
  profile: UserProfile | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<UserProfile>;
}

export function useProfile(apiBaseUrl: string): UseProfileReturn {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await apiClient<UserProfile>(apiBaseUrl, "/profile");
      setProfile(data);
    } catch (err) {
      setError((err as Error).message);
      setProfile(null);
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsLoading(true);
      setError(null);

      try {
        const data = await apiClient<UserProfile>(apiBaseUrl, "/profile");
        if (!cancelled) {
          setProfile(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError((err as Error).message);
          setProfile(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl]);

  const updateProfile = useCallback(async (updates: Partial<UserProfile>): Promise<UserProfile> => {
    const data = await apiClient<UserProfile>(apiBaseUrl, "/profile", {
      method: "PUT",
      body: JSON.stringify(updates),
    });
    setProfile(data);
    return data;
  }, [apiBaseUrl]);

  return { profile, isLoading, error, refresh, updateProfile };
}
